import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Modal } from "../ui/Modal";
import { Button } from "../ui/Button";
import { Spinner } from "../ui/Spinner";
import { Alert } from "../ui/Alert";
import { Input } from "../ui/Input";
import { galleryApi, GalleryImage } from "../../services/galleryApi";
import { getImageUrl } from "../../utils/imageUtils";
import { FaSearch, FaImages } from "react-icons/fa";

interface GalleryImageSelectorProps {
  onSelect: (imageUrl: string) => void;
  label?: string;
  buttonText?: string;
}

export const GalleryImageSelector: React.FC<GalleryImageSelectorProps> = ({
  onSelect,
  label = "Select from Gallery",
  buttonText = "Select from Gallery",
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [images, setImages] = useState<GalleryImage[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    if (isOpen) {
      fetchImages();
    }
  }, [isOpen]);

  const fetchImages = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await galleryApi.getImages("catalog", false);
      setImages(data);
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to fetch gallery images");
    } finally {
      setLoading(false);
    }
  };

  const filteredImages = images.filter((image) =>
    image.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelect = (image: GalleryImage) => {
    onSelect(image.url);
    setIsOpen(false);
    setSearchTerm("");
  };

  return (
    <>
      {label ? (
        <div>
          <label className="block text-sm font-medium mb-2">{label}</label>
          <Button
            type="button"
            variant="outline"
            onClick={() => setIsOpen(true)}
            className="w-full"
          >
            <FaImages className="mr-2" />
            {buttonText}
          </Button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setIsOpen(true)}
          className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white hover:bg-blue-700 transition-colors rounded text-sm"
        >
          <FaImages />
          {buttonText}
        </button>
      )}

      <Modal
        isOpen={isOpen}
        onClose={() => {
          setIsOpen(false);
          setSearchTerm("");
        }}
        title={t("admin.gallery.selectFromGallery")}
      >
        <div className="space-y-4">
          {error && (
            <Alert variant="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <div className="relative">
            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <Input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={t("admin.gallery.searchPlaceholder")}
              className="pl-10"
            />
          </div>

          {loading ? (
            <div className="flex justify-center py-8">
              <Spinner />
            </div>
          ) : filteredImages.length === 0 ? (
            <p className="text-center text-gray-500 py-8">
              {searchTerm
                ? t("admin.gallery.noImagesFound")
                : t("admin.gallery.noCatalogImages")}
            </p>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 max-h-96 overflow-y-auto">
              {filteredImages.map((image) => (
                <div
                  key={image._id}
                  className="border rounded-lg p-2 cursor-pointer hover:border-blue-500 transition-colors"
                  onClick={() => handleSelect(image)}
                >
                  <img
                    src={getImageUrl(image.thumbnailUrl || image.url)}
                    alt={image.name}
                    className="w-full h-24 object-cover rounded mb-2"
                  />
                  <div className="text-xs font-medium truncate">{image.name}</div>
                </div>
              ))}
            </div>
          )}

          <div className="flex justify-end">
            <Button
              variant="secondary"
              onClick={() => {
                setIsOpen(false);
                setSearchTerm("");
              }}
            >
              {t("admin.cancel")}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};
